import {createSlice, createSelector} from '@reduxjs/toolkit';
import spacetime from 'spacetime';
import _ from 'lodash';
import hash from 'object-hash';

const CHECK_INITIAL_STATE = {
  jobId: null,
  asOf: null,
  items: null,
  openItems: null,
  numItems: 0,
  numOpenItems: 0,
  isFetching: false,
  isError: false,
  errorMessage: null,
  isVisible: true,
};

const INITIAL_STATE = {
  documentId: null,
  numOpenItems: 0,
  copilotActionMessage: null,
  copilotActionSuccess: false,
  copilotReviewComplete: false,
  copilotActionError: false,
  copilotErrorMessage: null,
  footings: _.clone(CHECK_INITIAL_STATE),
  inconsistencies: _.clone(CHECK_INITIAL_STATE),
  currentPage: null,
  tableFormatting: {
    resultsByDocument: {},
    isFetching: false,
    showSnackbar: false,
    snackbarMessage: '',
    snackbarType: 'fetch', // 'fetch' | 'accept' | 'reject'
  },
};

// TODO: v2 Remove once use-new-ai-api-contract can be removed
const _parseListOfInconsistencies = ({inconsistencies}) => {
  // ENG-2179 - filter out insonsistencies that have been dismissed
  const _inconsistencies = _.keyBy(
    _.map(_.filter(inconsistencies, {dismissed: false}), (_inconsistency) => {
      const _items = _.sortBy(
        _.map(_inconsistency.items, (_item) => ({
          ..._item,
        })),
        'page_number'
      );

      return {
        ..._inconsistency,
        items: _items,
        pageNumber: _items[0].page_number,
        page_number: _items[0].page_number,
        resolved: false,
        type: 'inconsistencies',
        cardId: _inconsistency.id,
      };
    }),
    'id'
  );
  if (process.env.NODE_ENV === 'development') {
    console.log('parseListOfInconsistencies', _inconsistencies);
  }
  return _inconsistencies;
};

const _parseListOfInconsistenciesV1 = (inconsistencies) => {
  const _inconsistencies = {};
  _.forEach(
    _.cloneDeep(
      _.filter(inconsistencies.with_discrepancies, {dismissed: false})
    ),
    (_inconsistency) => {
      _inconsistency.resolved = false;
      _inconsistency.type = 'inconsistencies';
      const _itemDiscrepancies = {};
      _.forEach(_inconsistency.list_of_discrepancies, (_values, _key) => {
        _.forEach(_values, (_item) => {
          _.forEach(_item.items, (_item) => {
            _itemDiscrepancies[hash(_item)] = _item;
            _item.id = _.get(_item, 'table_id', _item.id);
          });
        });
      });
      _inconsistency.items = _.sortBy(
        _.values(_itemDiscrepancies),
        'page_number'
      );
      _inconsistency.pagenumber = _inconsistency.items[0].page_number;
      _inconsistency.cardId = _inconsistency.id;
      _inconsistencies[_inconsistency.id] = _inconsistency;
    }
  );
  if (process.env.NODE_ENV === 'development') {
    console.log('parseListOfInconsistencies', _inconsistencies);
  }
  return _inconsistencies;
};

const _parseFootingIssues = (footingIssues) => {
  const _items = {};
  const _openItems = {};
  console.log('parseFootingIssues', {footingIssues});
  _.forEach(
    _.cloneDeep(_.filter(footingIssues, {dismissed: false})),
    (_footingIssue) => {
      _.forEach(_footingIssue.footings, (_footing) => {
        if (_footing.dismissed) {
          return;
        }
        if (
          _footing.calculated_value === '' &&
          _footing.presented_value === ''
        ) {
          return;
        }
        const _item = {
          ..._footing,
          ..._.pick(_footingIssue, ['table_id', 'table_page_number']),
          type: 'footings',
          resolved: false,
          pageNumber: _footingIssue.page_number,
          tableId: _footingIssue.table_id,
          id: _footing.id,
          cardId: _footing.id,
        };
        _items[_item.id] = _item;
        if (!_item.is_footing_correct) {
          _openItems[_item.id] = _item;
        }
      });
    }
  );
  if (process.env.NODE_ENV === 'development') {
    console.log('parseFootingIssues', {items: _items, openItems: _openItems});
  }
  return {items: _items, openItems: _openItems};
};

const _setPayload = (
  checkType,
  state,
  jobId,
  data,
  isSuccess,
  isFetching,
  isError,
  error,
  v2
) => {
  const _payload = state;
  if (!_.isNil(jobId)) {
    _payload.jobId = jobId;
  }
  if (!_.isNil(isSuccess)) {
    _payload.isSuccess = isSuccess;
  }
  if (!_.isNil(isFetching)) {
    _payload.isFetching = isFetching;
  }
  if (!_.isNil(isError)) {
    _payload.isError = isError;
  }
  _payload.errorMessage = error;

  if (!isFetching && isSuccess) {
    _payload.asOf = spacetime.now().epoch;
    if (checkType === 'footings') {
      const {items, openItems} = _parseFootingIssues(data);
      _payload.items = items;
      _payload.openItems = openItems;
      _payload.numItems = _.values(items).length;
      _payload.numOpenItems = _.values(openItems).length;
    } else {
      if (v2) {
        const items = _parseListOfInconsistencies(data);
        _payload.items = items;
        _payload.openItems = items;
        _payload.numItems = _.values(items).length;
        _payload.numOpenItems = _.values(items).length;
      } else {
        const items = _parseListOfInconsistenciesV1(data);
        _payload.items = items;
        _payload.openItems = items;
        _payload.numItems = _.values(items).length;
        _payload.numOpenItems = _.values(items).length;
      }
    }
  }

  return _payload;
};

export const copilotSlice = createSlice({
  name: 'copilot',
  initialState: INITIAL_STATE,
  reducers: {
    hideActionError: (state) => {
      state.copilotActionError = false;
      state.copilotErrorMessage = null;
    },
    hideReviewComplete: (state) => {
      state.copilotReviewComplete = false;
    },
    hideActionSuccess: (state) => {
      state.copilotActionSuccess = false;
      state.copilotActionMessage = null;
    },
    markItemDismissed: (state, action) => {
      const {checkType, id, copilotActionMessage} = action.payload;
      const _payload = state[checkType];
      const _items = _payload.items;
      const _openItems = _payload.openItems;
      delete _items[id];
      delete _openItems[id];
      _payload.numOpenItems = _.values(_openItems).length;
      _payload.numItems = _.values(_items).length;
      _payload.items = _items;
      _payload.openItems = _openItems;
      state[checkType] = _payload;
      state.copilotActionMessage = copilotActionMessage;
      state.copilotActionSuccess = true;
    },
    markItemReviewed: (state, action) => {
      const {checkType, id} = action.payload;
      const _payload = state[checkType];
      const _items = _payload.items;
      const _openItems = _payload.openItems;
      _items[id].resolved = true;
      delete _openItems[id];
      _payload.numOpenItems = _.values(_openItems).length;
      _payload.items = _items;
      _payload.openItems = _openItems;
      state[checkType] = _payload;
      state.copilotActionSuccess = true;
    },
    setDocumentId: (state, action) => {
      state.documentId = action.payload.documentId;
    },
    setPayload: (state, action) => {
      const {
        checkType,
        jobId,
        data,
        isSuccess,
        isFetching,
        isError,
        error,
        v2,
      } = action.payload;
      state[checkType] = _setPayload(
        checkType,
        state[checkType],
        jobId,
        data,
        isSuccess,
        isFetching,
        isError,
        error,
        v2
      );
      if (isSuccess) {
        state.copilotReviewComplete = true;
        state.numOpenItems =
          state.footings.numOpenItems + state.inconsistencies.numOpenItems;
      }
    },
    setIsVisible: (state, action) => {
      const {checkType, isVisible} = action.payload;
      state[checkType].isVisible = isVisible;
    },
    showActionError: (state, action) => {
      state.copilotActionError = true;
      state.copilotErrorMessage = _.get(
        action,
        'payload.copilotErrorMessage',
        null
      );
    },
    showActionSuccess: (state, action) => {
      state.copilotActionSuccess = true;
      state.copilotActionMessage = _.get(
        action,
        'payload.copilotActionMessage',
        null
      );
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload.currentPage;
    },
    setTableFormattingSuggestions: (state, action) => {
      const {tables, fetchedAt, documentId} = action.payload;
      if (!documentId) {
        console.warn('setTableFormattingSuggestions: documentId is required');
        state.tableFormatting.isFetching = false;
        return;
      }
      if (!state.tableFormatting.resultsByDocument[documentId]) {
        state.tableFormatting.resultsByDocument[documentId] = {
          tablesWithSuggestions: [],
          fetchedAt: null,
        };
      }
      state.tableFormatting.resultsByDocument[
        documentId
      ].tablesWithSuggestions = tables;
      state.tableFormatting.resultsByDocument[documentId].fetchedAt = fetchedAt;
      state.tableFormatting.isFetching = false;

      // Show snackbar when suggestions are fetched
      const totalSuggestions = tables.reduce(
        (sum, table) => sum + table.suggestions.length,
        0
      );
      if (totalSuggestions > 0) {
        const totalTables = tables.length;
        state.tableFormatting.snackbarMessage =
          totalTables === 1
            ? `Found ${totalSuggestions} formatting suggestion${totalSuggestions !== 1 ? 's' : ''}`
            : `Found ${totalSuggestions} formatting suggestions across ${totalTables} table${totalTables !== 1 ? 's' : ''}`;
        state.tableFormatting.snackbarType = 'fetch';
        state.tableFormatting.showSnackbar = true;
      }
    },
    setTableFormattingFetching: (state, action) => {
      state.tableFormatting.isFetching = action.payload.isFetching;
    },
    acceptTableFormattingSuggestion: (state, action) => {
      const {suggestionId, tableId, documentId} = action.payload;
      if (!documentId) {
        console.warn('acceptTableFormattingSuggestion: documentId is required');
        return;
      }
      const documentResults =
        state.tableFormatting.resultsByDocument[documentId];
      if (documentResults) {
        const table = documentResults.tablesWithSuggestions.find(
          (t) => t.tableId === tableId
        );
        if (table) {
          const suggestion = table.suggestions.find(
            (s) => s.id === suggestionId
          );
          if (suggestion) {
            suggestion.accepted = true;
            state.tableFormatting.snackbarMessage = 'Suggestion accepted';
            state.tableFormatting.snackbarType = 'accept';
            state.tableFormatting.showSnackbar = true;
          }
        }
      }
    },
    rejectTableFormattingSuggestion: (state, action) => {
      const {suggestionId, tableId, documentId} = action.payload;
      if (!documentId) {
        console.warn('rejectTableFormattingSuggestion: documentId is required');
        return;
      }
      const documentResults =
        state.tableFormatting.resultsByDocument[documentId];
      if (documentResults) {
        const table = documentResults.tablesWithSuggestions.find(
          (t) => t.tableId === tableId
        );
        if (table) {
          const suggestion = table.suggestions.find(
            (s) => s.id === suggestionId
          );
          if (suggestion) {
            suggestion.rejected = true;
            state.tableFormatting.snackbarMessage = 'Suggestion rejected';
            state.tableFormatting.snackbarType = 'reject';
            state.tableFormatting.showSnackbar = true;
          }
        }
      }
    },
    acceptAllTableFormattingSuggestions: (state, action) => {
      const {tableId, documentId} = action.payload;
      if (!documentId) {
        console.warn(
          'acceptAllTableFormattingSuggestions: documentId is required'
        );
        return;
      }
      const documentResults =
        state.tableFormatting.resultsByDocument[documentId];
      if (documentResults) {
        const table = documentResults.tablesWithSuggestions.find(
          (t) => t.tableId === tableId
        );
        if (table) {
          const unprocessedCount = table.suggestions.filter(
            (s) => !s.accepted && !s.rejected
          ).length;
          table.suggestions.forEach((s) => {
            s.accepted = true;
          });
          if (unprocessedCount > 0) {
            state.tableFormatting.snackbarMessage =
              unprocessedCount === 1
                ? 'Suggestion accepted'
                : `Accepted ${unprocessedCount} suggestions`;
            state.tableFormatting.snackbarType = 'accept';
            state.tableFormatting.showSnackbar = true;
          }
        }
      }
    },
    rejectAllTableFormattingSuggestions: (state, action) => {
      const {tableId, documentId} = action.payload;
      if (!documentId) {
        console.warn(
          'rejectAllTableFormattingSuggestions: documentId is required'
        );
        return;
      }
      const documentResults =
        state.tableFormatting.resultsByDocument[documentId];
      if (documentResults) {
        const table = documentResults.tablesWithSuggestions.find(
          (t) => t.tableId === tableId
        );
        if (table) {
          const unprocessedCount = table.suggestions.filter(
            (s) => !s.accepted && !s.rejected
          ).length;
          table.suggestions.forEach((s) => {
            s.rejected = true;
          });
          if (unprocessedCount > 0) {
            state.tableFormatting.snackbarMessage =
              unprocessedCount === 1
                ? 'Suggestion rejected'
                : `Rejected ${unprocessedCount} suggestions`;
            state.tableFormatting.snackbarType = 'reject';
            state.tableFormatting.showSnackbar = true;
          }
        }
      }
    },
    hideTableFormattingSnackbar: (state) => {
      state.tableFormatting.showSnackbar = false;
    },
    acceptAllTableFormattingSuggestionsGlobal: (state, action) => {
      const {documentId} = action.payload;
      if (!documentId) {
        console.warn(
          'acceptAllTableFormattingSuggestionsGlobal: documentId is required'
        );
        return;
      }
      const documentResults =
        state.tableFormatting.resultsByDocument[documentId];
      if (documentResults) {
        let totalAccepted = 0;
        let totalTables = 0;
        documentResults.tablesWithSuggestions.forEach((table) => {
          const unprocessedCount = table.suggestions.filter(
            (s) => !s.accepted && !s.rejected
          ).length;
          if (unprocessedCount > 0) {
            totalTables++;
            totalAccepted += unprocessedCount;
            table.suggestions.forEach((s) => {
              if (!s.accepted && !s.rejected) {
                s.accepted = true;
              }
            });
          }
        });
        if (totalAccepted > 0) {
          state.tableFormatting.snackbarMessage =
            totalTables === 1
              ? totalAccepted === 1
                ? 'Accepted 1 suggestion'
                : `Accepted ${totalAccepted} suggestions`
              : `Accepted ${totalAccepted} suggestions across ${totalTables} tables`;
          state.tableFormatting.snackbarType = 'accept';
          state.tableFormatting.showSnackbar = true;
        }
      }
    },
    rejectAllTableFormattingSuggestionsGlobal: (state, action) => {
      const {documentId} = action.payload;
      if (!documentId) {
        console.warn(
          'rejectAllTableFormattingSuggestionsGlobal: documentId is required'
        );
        return;
      }
      const documentResults =
        state.tableFormatting.resultsByDocument[documentId];
      if (documentResults) {
        let totalRejected = 0;
        let totalTables = 0;
        documentResults.tablesWithSuggestions.forEach((table) => {
          const unprocessedCount = table.suggestions.filter(
            (s) => !s.accepted && !s.rejected
          ).length;
          if (unprocessedCount > 0) {
            totalTables++;
            totalRejected += unprocessedCount;
            table.suggestions.forEach((s) => {
              if (!s.accepted && !s.rejected) {
                s.rejected = true;
              }
            });
          }
        });
        if (totalRejected > 0) {
          state.tableFormatting.snackbarMessage =
            totalTables === 1
              ? totalRejected === 1
                ? 'Rejected 1 suggestion'
                : `Rejected ${totalRejected} suggestions`
              : `Rejected ${totalRejected} suggestions across ${totalTables} tables`;
          state.tableFormatting.snackbarType = 'reject';
          state.tableFormatting.showSnackbar = true;
        }
      }
    },
    reset: (state) => {
      // Preserve table formatting cache across resets (e.g., when switching documents)
      const preservedTableFormatting = {
        ...state.tableFormatting,
        showSnackbar: false,
        snackbarMessage: '',
        snackbarType: 'fetch',
      };
      return {
        ...INITIAL_STATE,
        tableFormatting: preservedTableFormatting,
      };
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  hideActionError,
  hideActionSuccess,
  hideReviewComplete,
  markItemDismissed,
  markItemReviewed,
  reset,
  setDocumentId,
  setIsVisible,
  setPayload,
  showActionError,
  showActionSuccess,
  setCurrentPage,
  setTableFormattingSuggestions,
  setTableFormattingFetching,
  acceptTableFormattingSuggestion,
  rejectTableFormattingSuggestion,
  acceptAllTableFormattingSuggestions,
  rejectAllTableFormattingSuggestions,
  acceptAllTableFormattingSuggestionsGlobal,
  rejectAllTableFormattingSuggestionsGlobal,
  hideTableFormattingSnackbar,
} = copilotSlice.actions;

export default copilotSlice.reducer;

const selectCopilot = (state) => state.copilot;
const selectDocumentId = (state) => state.copilot.documentId;

export const selectTableFormattingState = createSelector(
  [selectCopilot, selectDocumentId],
  (copilot, documentId) => {
    const tableFormatting = copilot.tableFormatting || {
      resultsByDocument: {},
      isFetching: false,
    };

    const documentResults = documentId
      ? tableFormatting.resultsByDocument[documentId]
      : null;

    return {
      tablesWithSuggestions: documentResults?.tablesWithSuggestions ?? [],
      fetchedAt: documentResults?.fetchedAt ?? null,
      isFetching: tableFormatting.isFetching ?? false,
    };
  }
);
